import { useEffect, useRef, useState, useCallback } from "react";
import { Image as ImageIcon, Upload, Trash2, Loader2, RefreshCw, Info } from "lucide-react";
import { toast } from "sonner";
import {
  adminGetCmsSlots, adminUploadCmsImage, adminDeleteCmsImage,
  CATEGORY_NAV, BRAND_NAV,
} from "@/lib/api";
import { refreshCmsOverrides } from "@/lib/cms";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

// Friendly labels for each slot key.
const CAT_LABEL = Object.fromEntries(CATEGORY_NAV.map((c) => [`category:${c.slug}`, c.name]));
const BRAND_LABEL = Object.fromEntries(BRAND_NAV.map((b) => [`brand:${b.slug}`, b.name]));

const labelFor = (slot) => {
  if (slot === "hero:main") return "Homepage Hero";
  return CAT_LABEL[slot] || BRAND_LABEL[slot] || slot;
};

// Override urls come back relative (/api/cms/media/...). Prefix the backend host
// so previews render reliably regardless of admin route.
const mediaSrc = (url) => {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  return `${BACKEND_URL}${url}`;
};

const SlotCard = ({ slot, current, aspect, onChanged }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const url = current?.url;

  const pick = () => inputRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    if (file.size > 12 * 1024 * 1024) { toast.error("Image too large (max 12MB)"); return; }
    setBusy(true);
    try {
      await adminUploadCmsImage({ slot, file });
      toast.success(`${labelFor(slot)} image updated`);
      await refreshCmsOverrides();
      await onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    setBusy(true);
    try {
      await adminDeleteCmsImage(slot);
      toast.success(`${labelFor(slot)} reset to default`);
      await refreshCmsOverrides();
      await onChanged?.();
    } catch {
      toast.error("Could not reset image");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card" data-testid={`cms-slot-${slot}`}>
      <div className={`relative ${aspect} w-full bg-secondary`}>
        {url ? (
          <img
            src={mediaSrc(url)}
            alt={labelFor(slot)}
            className="h-full w-full object-cover"
            data-testid={`cms-slot-image-${slot}`}
          />
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center gap-1.5 text-muted-foreground">
            <ImageIcon className="h-6 w-6 opacity-50" />
            <span className="text-[11px] uppercase tracking-[0.16em]">Auto / default</span>
          </div>
        )}
        {busy && (
          <div className="absolute inset-0 grid place-items-center bg-background/60 backdrop-blur-sm">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
          </div>
        )}
      </div>
      <div className="flex items-center justify-between gap-2 px-3.5 py-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">{labelFor(slot)}</p>
          <p className="truncate font-mono text-[10px] text-muted-foreground">{slot}</p>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {url && (
            <Button variant="ghost" size="icon" onClick={reset} disabled={busy}
              aria-label="Reset to default" data-testid={`cms-reset-${slot}`}>
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={pick} disabled={busy} data-testid={`cms-upload-${slot}`}>
            <Upload className="mr-1.5 h-3.5 w-3.5" /> {url ? "Replace" : "Upload"}
          </Button>
        </div>
      </div>
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif"
        className="hidden" onChange={onFile} data-testid={`cms-input-${slot}`} />
    </div>
  );
};

export const CMSManager = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await adminGetCmsSlots());
    } catch {
      toast.error("Failed to load CMS slots");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const current = data?.current || {};

  const Section = ({ title, slots, aspect }) => (
    <div className="rounded-2xl border border-border bg-card/40 p-4 sm:p-5">
      <h3 className="text-sm font-semibold">{title}</h3>
      <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {slots.map((slot) => (
          <SlotCard key={slot} slot={slot} current={current[slot]} aspect={aspect} onChanged={load} />
        ))}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="cms-manager-loading">
        {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-56 w-full rounded-xl" />)}
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="cms-manager">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-xl">Homepage Imagery</h2>
          <p className="text-sm text-muted-foreground">Upload custom images for the hero, category cards and brand tiles. Leave blank to use the automatic images.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="cms-refresh-button">
          <RefreshCw className="mr-2 h-4 w-4" /> Refresh
        </Button>
      </div>

      <div className="flex items-start gap-2 rounded-lg border border-dashed border-border bg-secondary/40 px-3.5 py-2.5 text-xs text-muted-foreground">
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
        <span>Images are auto-optimised to fast WebP. Recommended: landscape JPG/PNG, at least 1200px wide for categories/brands and 1800px for the hero. Max 12MB.</span>
      </div>

      <Section title="Hero Banner" slots={data?.hero || []} aspect="aspect-[16/7]" />
      <Section title="Category Cards" slots={data?.categories || []} aspect="aspect-[4/3]" />
      <Section title="Brand Tiles" slots={data?.brands || []} aspect="aspect-[4/3]" />
    </div>
  );
};
